<template>
  <div class="asd20-school-directory">
    <div class="asd20-school-directory__toolbar">
      <asd20-search-field v-model="keywords" />
      <div class="toolbar-wrapper">
        <asd20-button
          class="filter-button"
          v-if="this.$mq === 'sm' || this.$mq === 'md'"
          label="Filters"
          icon="filter"
          size="sm"
          transparent
          horizontal
          @click.native="
            () => {
              filtersOpen = true
              isActive = true
            }
          "
        >
          <asd20-badge
            :visible="!!totalFiltersApplied"
            :value="totalFiltersApplied"
          />
        </asd20-button>
        <asd20-checkbox
          :value="view === 'list'"
          class="viewToggle"
          label="Map View"
          description="List View"
          style="order: 3; flex-grow: 0;"
          @input="toggleView"
        />
      </div>
      <asd20-modal
        :isActive="isActive"
        v-model="filtersOpen"
        title="Filter"
        icon="filter"
        dismissable
        windowed
      >
        <p slot="footer" style="padding: 1rem">
          {{ schools.length }} school{{
            `${schools.length === 1 ? '' : 's'}`
          }}
          found matching {{ totalFiltersApplied }} filter{{
            `${totalFiltersApplied === 1 ? '' : 's'}`
          }}.
        </p>

        <asd20-button
          v-if="totalFiltersApplied"
          slot="footer"
          style="margin-left: auto"
          transparent
          horizontal
          size="sm"
          icon="close"
          label="Clear"
          @click.native="clearFilters"
        />

        <asd20-school-filters
          :view.sync="view"
          :max-distance.sync="maxDistance"
          :selected-education-levels.sync="selectedEducationLevels"
          :selected-educational-attributes.sync="selectedEducationalAttributes"
          :selected-arts-offerings.sync="selectedArtsOfferings"
          :selected-athletics-offerings.sync="selectedAthleticsOfferings"
          :facets="facets"
          @update:view="onViewUpdate"
        />
      </asd20-modal>
    </div>

    <mq-layout class="asd20-school-directory__desktop-layout" mq="lg+">
      <div class="asd20-school-directory__desktop-filters">
        <h2>
          <asd20-icon name="filter" size="sm" />
          Filters
          <asd20-badge
            :visible="!!totalFiltersApplied"
            :value="totalFiltersApplied"
            static
          />
          <asd20-button
            v-if="totalFiltersApplied"
            slot="footer"
            transparent
            horizontal
            size="sm"
            icon="close"
            label="Clear"
            @click.native="clearFilters"
          />
        </h2>
        <asd20-viewport scrollable>
          <asd20-school-filters
            :view.sync="view"
            :max-distance.sync="maxDistance"
            :selected-education-levels.sync="selectedEducationLevels"
            :selected-educational-attributes.sync="
              selectedEducationalAttributes
            "
            :selected-arts-offerings.sync="selectedArtsOfferings"
            :selected-athletics-offerings.sync="selectedAthleticsOfferings"
            :facets="facets"
          />
        </asd20-viewport>
      </div>

      <asd20-school-directory-list-view
        v-if="view === 'list' && !transitioning"
        :schools="schools"
        @select="onSchoolSelect"
      />
      <asd20-school-directory-map-view
        v-if="view === 'map' && !transitioning"
        :schools="schools"
        @select="onSchoolSelect"
      />
    </mq-layout>

    <mq-layout :mq="['sm', 'md']" class="asd20-school-mobile-layout">
      <asd20-school-directory-list-view
        v-if="view === 'list' && !transitioning"
        :schools="schools"
        @select="onSchoolSelect"
      />

      <asd20-school-directory-map-view
        v-if="view === 'map' && !transitioning"
        :schools="schools"
        @select="onSchoolSelect"
      />
    </mq-layout>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify'
import debounce from 'lodash.debounce'
import Asd20Viewport from '@asd20/ui/src/components/atoms/Asd20Viewport'
import Asd20Modal from '@asd20/ui/src/components/molecules/Asd20Modal'
import Asd20SchoolFilters from '@/components/organisms/Asd20SchoolFilters'
import Asd20SearchField from '@asd20/ui/src/components/molecules/Asd20SearchField'
import Asd20Badge from '@asd20/ui/src/components/atoms/Asd20Badge'
import Asd20Button from '@asd20/ui/src/components/atoms/Asd20Button'
import Asd20Checkbox from '@asd20/ui/src/components/atoms/Asd20Checkbox'
import Asd20Icon from '@asd20/ui/src/components/atoms/Asd20Icon'
import Asd20Card from '@asd20/ui/src/components/molecules/Asd20Card'
import Asd20Accordion from '@asd20/ui/src/components/molecules/Asd20Accordion'
import Asd20SchoolDirectoryListView from '@/components/organisms/Asd20SchoolDirectoryListView'
import Asd20SchoolDirectoryMapView from '@/components/organisms/Asd20SchoolDirectoryMapView'
import syncronizedQueryParameters from '../../mixins/syncronizedQueryParameters'

export default {
  name: 'Asd20SchoolDirectory',
  components: {
    Asd20Viewport,
    Asd20Modal,
    Asd20SearchField,
    Asd20Badge,
    Asd20Button,
    Asd20Icon,
    Asd20Checkbox,
    Asd20Card,
    Asd20Accordion,
    Asd20SchoolFilters,
    Asd20SchoolDirectoryListView,
    Asd20SchoolDirectoryMapView,
  },

  mixins: [syncronizedQueryParameters],

  props: {
    transitioning: { type: Boolean, default: false },
  },

  data: () => ({
    filtersOpen: false,
    isActive: false,
    view: 'list',
    syncronizedQueryParameters: [
      {
        valueKey: 'selectedEducationLevels',
        queryKey: 'selectedEducationLevels',
        forceArray: true,
      },
      {
        valueKey: 'selectedEducationalAttributes',
        queryKey: 'selectedEducationalAttributes',
        forceArray: true,
      },
      {
        valueKey: 'selectedArtsOfferings',
        queryKey: 'selectedArtsOfferings',
        forceArray: true,
      },
      {
        valueKey: 'selectedAthleticsOfferings',
        queryKey: 'selectedAthleticsOfferings',
        forceArray: true,
      },
    ],
  }),

  computed: {
    totalFiltersApplied: get('schools/totalFiltersApplied'),
    searchPayload: get('schools/searchPayload'),
    schools: get('schools/schoolsWithMetadata'),
    facets: get('schools/facets'),
    suggestions: get('schools/suggestions'),
    keywords: sync('schools/keywords'),
    maxDistance: sync('schools/maxDistance'),
    selectedEducationLevels: sync('schools/selectedEducationLevels'),
    selectedEducationalAttributes: sync(
      'schools/selectedEducationalAttributes'
    ),
    selectedArtsOfferings: sync('schools/selectedArtsOfferings'),
    selectedAthleticsOfferings: sync('schools/selectedAthleticsOfferings'),
  },

  watch: {
    searchPayload: {
      handler: function() {
        this.querySchools()
      },
      immediate: true,
    },
  },

  mounted() {
    this.$store.dispatch('schools/getAllSchools')
  },

  methods: {
    clearFilters() {
      this.$store.dispatch('schools/clearFilters')
    },
    querySchools: debounce(function() {
      this.$store.dispatch('schools/querySchools')
    }, 200),
    onSchoolSelect(school) {
      if (this.$router) {
        this.$router.push(`/${school.abbreviation.toLowerCase()}`)
      }
    },
    toggleView() {
      this.view = this.view === 'list' ? 'map' : 'list'
    },
    onViewUpdate(val) {
      this.view = val
      if (val === 'map' && this.filtersOpen) {
        this.filtersOpen = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@asd20/ui/src/design/_mixins.scss';
@import '@asd20/ui/src/design/_variables.scss';

.asd20-school-directory {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  &__toolbar {
    border-bottom: 1px solid asd20-swatch('background-alt');
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    .asd20-search-field {
      flex-grow: 1;
      border-bottom: 1px solid rgb(235, 235, 235);
      // input {
      //   background: var(--color__accent-t90);
      // }
    }
    .toolbar-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      .filter-button {
        font-size: 0.65rem !important;
        background: #ecf0f5;
        margin: 0.25rem;
        .asd20-badge {
          top: 0;
          right: 0;
        }
      }
      .viewToggle {
        width: inherit;
        margin-left: 1rem;
        span:nth-of-type(1) {
          text-align: right;
          font-size: 0.65rem;
          // font-weight: 700;
          font-family: var(--website-typography__font-family-headlines);
        }
        span:nth-of-type(2) {
          order: 4;
          font-size: 0.65rem;
          // font-weight: 700;
          font-family: var(--website-typography__font-family-headlines);
          margin-left: 1rem;
        }
      }
    }
    .asd20-modal-container {
      z-index: 1001;
    }
  }
}

.asd20-school-mobile-layout {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
}
@media (min-width: 600px) {
  .asd20-school-directory {
    &__toolbar {
      flex-direction: row;
      align-items: center;
      .asd20-search-field {
        border-bottom: none;
      }
      .toolbar-wrapper {
        .filter-button {
          font-size: 0.85rem !important;
        }
        .viewToggle {
          span:nth-of-type(1) {
            font-size: 0.85rem;
          }
          span:nth-of-type(2) {
            font-size: 0.85rem;
          }
        }
      }
    }
  }
}

@media (min-width: 1024px) {
  .asd20-school-directory {
    overflow: hidden;
    &__desktop-layout {
      display: block;
      position: relative;
      flex-grow: 1;
      overflow: hidden;
    }
    &__list-view {
      position: absolute !important;
      left: 25rem;
      top: 0;
      right: 0;
      bottom: 0;
    }
    &__map-view {
      position: absolute;
      left: 25rem;
      top: 0;
      right: 0;
      bottom: 0;
    }
    &__desktop-filters {
      position: absolute;
      left: 0;
      top: 0;
      right: calc(100% - 25rem);
      bottom: 0;

      display: flex;
      box-sizing: border-box;
      flex-direction: column;
      overflow: hidden;
      height: 100%;
      background: white;
      border-right: 1px solid asd20-swatch('background-alt');
      order: 2;

      h2 {
        position: relative;
        display: flex;
        align-items: center;
        flex-shrink: 0;
        margin: 0;
        font-size: 1rem;
        color: asd20-swatch('background-dark');

        & > .asd20-icon {
          margin: 0 space(0.5);
          padding: space(0.5) 0;
        }

        & > .asd20-badge {
          margin-left: space(0.5);
          margin-right: space(0.5);
        }

        .asd20-button {
          margin-left: auto;
          margin-right: space(0.5);
          order: 3;
        }
      }
    }
  }
}
</style>
